import React, { useCallback, useState } from 'react';
import { Button, Modal } from 'gatorcomponents';
import * as Styles from './ModalFeaturePro.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import useLocale from '@/hooks/useLocale';
import { IconCrown } from '../Icons/IconCrown';
import GradientButton from '../GradientButton';
import HGImageFeaturePro from './HGImageFeaturePro';
import { ID_PRO_FEATURES } from '../../studio.enum';
import { useResize } from '@/hooks/useResize';
import { IconAiPencil } from '../Icons/IconAiPencil';
import { STEPS } from './ModalFeaturePro.types';
import { PurchaseTrial } from './components/PurchaseTrial';
import { useHistory } from 'react-router';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';
import { homeActions } from '@/redux/modules';
import { useDispatch } from 'react-redux';
import { useStudio } from '@/hooks/studio';
import { parseISO } from 'date-fns';

const ModalFeaturePro = ({
  setModalFeatureProToShow,
  isProPlan = false,
  modalId,
  trialAvailable = false,
  renewalDate = null,
  hasDiscount = false,
  productTrial,
  productPro,
  discount,
  provisionFreePlan,
  planPrice,
  purchaseTrialWithoutCc,
  loading,
}) => {
  const [currentStep, setCurrentStep] = useState(STEPS.INITIAL_STEP);

  const windowRect = useResize();
  const history = useHistory();
  const { studio: studioLocale, routes: routesLocale } = useLocale();
  const { checkoutTrialWithCreditCard, fastcheckoutLoading } = useStudio();
  const dispatch = useDispatch();

  const modalLocale = studioLocale.modalFeaturePro;

  const { width } = windowRect;
  const mobile = width < 960;

  const listFeaturesPro = modalLocale.listFeaturesPro || [];

  const getRenewalDate = () => {
    const dateFree = new Date();
    dateFree.setMonth(dateFree.getMonth() + 1);
    dateFree.setDate(1);

    const dateOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const formattedDateFree = dateFree.toLocaleDateString('pt-BR', dateOptions);
    const formattedDatePro = parseISO(renewalDate).toLocaleDateString('pt-BR', dateOptions);

    const textPro = modalLocale.textNoCredits.replace('%date%', formattedDatePro);
    const textFree = modalLocale.textNoCreditsFree.replace('%date%', formattedDateFree);

    return isProPlan ? textPro : textFree;
  };

  const handleCloseModal = useCallback(() => {
    if (ID_PRO_FEATURES.STUDIO_PRO_OFFER === modalId) {
      dispatch(homeActions.getHomeContent.reset());
    }

    setModalFeatureProToShow(false);
  }, [setModalFeatureProToShow, modalId, dispatch]);

  const getTitleModal = () => {
    if (ID_PRO_FEATURES.STUDIO_PRO === modalId && trialAvailable) {
      return modalLocale.title.STUDIO_PRO_TRIAL;
    }

    return modalLocale.title[modalId];
  };

  const redirectToCheckout = useCallback(({ pid }) => {
    checkoutTrialWithCreditCard({
      pid,
      coupon: discount.name,
      couponAvailable: hasDiscount,
    });
  }, [checkoutTrialWithCreditCard, discount, hasDiscount]);

  const handleGoPlan = () => {
    history.push(routesLocale.studio + routesLocale.plans);
  };

  const handleProFlow = () => {
    const pid = trialAvailable ? HgProducts.STUDIO_PRO_TRIAL : HgProducts.STUDIO_PRO;

    redirectToCheckout({ pid });
  };

  const handleTrialFlow = () => {
    if (hasDiscount) {
      setCurrentStep(STEPS.PURCHASE_TRIAL);
    }
  };

  const getTextDescription = () => {
    if (modalId === ID_PRO_FEATURES.STUDIO_PRO_OFFER) {
      return modalLocale.textStudioTrial;
    }

    if (modalId === ID_PRO_FEATURES.STUDIO_PRO && !trialAvailable) {
      return modalLocale.textStudioPro(planPrice);
    }

    if (modalId === ID_PRO_FEATURES.STUDIO_PRO && trialAvailable) {
      return modalLocale.textStudioProTrial;
    }

    return modalLocale.textStudioFree(planPrice);
  };


  const handlePurchaseTrialWithoutCc = useCallback(() => {
    purchaseTrialWithoutCc && purchaseTrialWithoutCc();
  }, [purchaseTrialWithoutCc]);

  const purchaseTrialProps = useCallback(() => ({
    locales: modalLocale.purchaseTrial,
    handleCloseModal,
    redirectToCheckout,
    purchaseTrialWithoutCc: handlePurchaseTrialWithoutCc,
    productTrial,
    productPro,
    discount,
    provisionFreePlan,
    fastcheckoutLoading: fastcheckoutLoading || loading,
  }), [
    redirectToCheckout,
    handleCloseModal,
    modalLocale.purchaseTrial,
    fastcheckoutLoading,
    handlePurchaseTrialWithoutCc,
    productTrial,
    productPro,
    discount,
    provisionFreePlan,
    loading,
  ]);

  const getModalMaxWidth = () => {
    if (currentStep === STEPS.INITIAL_STEP) {
      return mobile ? 600 : 960;
    }

    return 960;
  };

  return (
    <Modal
      maxHeight="100vh"
      maxWidth={getModalMaxWidth()}
      noHeader
      noContentPadding
      onClose={handleCloseModal}
      testId="Modal"
    >
      {currentStep === STEPS.INITIAL_STEP && (
        <Styles.ModalContent>
          {ID_PRO_FEATURES.NO_CREDITS !== modalId ? (
            <Styles.WrapperText modalId={modalId}>
              <Styles.TagPro>
                <IconCrown />
                {studioLocale.tag.label}
              </Styles.TagPro>
              <Styles.Title>
                {getTitleModal()}
              </Styles.Title>

              <Styles.Text>
                {getTextDescription()}
              </Styles.Text>

              <Styles.WrapperList modalId={modalId}>
                {Object.entries(listFeaturesPro).map(([key, value]) => (
                  <Styles.ListItem key={key}>
                    {value.icon}
                    {value.text}
                  </Styles.ListItem>
                ))}
              </Styles.WrapperList>

              <Styles.WrapperButton modalId={modalId}>
                {ID_PRO_FEATURES.STUDIO_PRO_OFFER === modalId ? (
                  <GradientButton
                    testId="upgrade-to-trial-button"
                    label={modalLocale.buttonOfferTrialLabel}
                    onClick={handleTrialFlow}
                    variant="primary"
                  />
                ) : (
                  <>
                    <Button
                      label={modalLocale.buttonGoPlansLabel}
                      onClick={handleGoPlan}
                      size="xlarge"
                      testId="button"
                      title={modalLocale.buttonGoPlansLabel}
                      type="button"
                      variant="secondary"
                      version="v2"
                      width={mobile ? 'full' : 'fit-content'}
                    />

                    <GradientButton
                      testId="upgrade-to-pro-button"
                      label={!trialAvailable ? modalLocale.buttonFreeLabel : modalLocale.buttonTrialLabel}
                      onClick={!trialAvailable ? handleProFlow : handleTrialFlow}
                      variant="primary"
                    />
                  </>
                )}
              </Styles.WrapperButton>

              <Styles.TextCancellation>{modalLocale.textCancellation}</Styles.TextCancellation>
            </Styles.WrapperText>
          ) : (
            <Styles.WrapperText modalId={modalId}>
              <Styles.WrapperTextPro>
                <Styles.TextPro>
                  {isProPlan
                    ? studioLocale.tag.youArePro
                    : studioLocale.studioTitle.free
                  }
                </Styles.TextPro>

                <IconAiPencil />
              </Styles.WrapperTextPro>
              <Styles.Title modalId={modalId}>{modalLocale.titleNoCredits}</Styles.Title>
              {renewalDate && <Styles.Text modalId={modalId}>{getRenewalDate()}</Styles.Text>}

              <Styles.WrapperButton modalId={modalId} isProPlan={isProPlan}>
                {isProPlan ? (
                  <Button
                    label={modalLocale.buttonOkLabel}
                    onClick={handleCloseModal}
                    size="xlarge"
                    testId="button"
                    title={modalLocale.buttonOkLabel}
                    type="button"
                    variant="primary"
                    version="v2"
                    width="fit-content"
                  />
                ) : (
                  <>
                    <Button
                      label={modalLocale.buttonNotNowLabel}
                      onClick={handleCloseModal}
                      size="xlarge"
                      testId="button"
                      title={modalLocale.buttonNotNowLabel}
                      type="button"
                      variant="secondary"
                      version="v2"
                      width="fit-content"
                    />
                    <GradientButton
                      testId="upgrade-to-pro-button"
                      label={modalLocale.buttonFreeLabel}
                      onClick={handleProFlow}
                      variant="primary"
                    />
                  </>
                )}
              </Styles.WrapperButton>
            </Styles.WrapperText>
          )}

          <Styles.WrapperImage modalId={modalId}>
            <HGImageFeaturePro modalId={modalId} />
          </Styles.WrapperImage>
        </Styles.ModalContent>
      )}

      {currentStep === STEPS.PURCHASE_TRIAL && <PurchaseTrial {...purchaseTrialProps()} />}
    </Modal>
  );
};

export default withErrorBoundary(ModalFeaturePro, 'ModalFeaturePro');
